import React, { useRef, useState, useMemo, useId } from 'react';

// Interactive dual-axis charging curve: Charging Power (kW, teal, left axis) and
// Current (A, amber, right axis) vs State of Charge (%). Hand-rolled SVG to match the
// site's chart style (no chart lib). Hovering shows a guide line + tooltip with the
// SoC / kW / A at the nearest point. Scales responsively via viewBox.
const TEAL = '#2dd4bf';
const TEAL_FILL = '#0d9488';
const AMBER = '#f59e0b';

export default function ChargingCurveChart({ curve = [], hasAmp = false, darkMode = true }) {
  const svgRef = useRef(null);
  const [hover, setHover] = useState(null); // index into curve
  const wmId = 'jxwm' + useId().replace(/:/g, ''); // tiled JXMEDIA watermark pattern

  const W = 840, H = 460;
  const m = { top: 22, right: hasAmp ? 66 : 26, bottom: 46, left: 58 };
  const plotW = W - m.left - m.right;
  const plotH = H - m.top - m.bottom;

  const geo = useMemo(() => {
    const socs = curve.map((p) => p.soc);
    const socMin = Math.min(...socs), socMax = Math.max(...socs);
    const kwMax = Math.max(...curve.map((p) => p.kw), 1) * 1.12;
    const ampVals = curve.filter((p) => p.amp !== null).map((p) => p.amp);
    const ampMax = (ampVals.length ? Math.max(...ampVals) : 1) * 1.12;
    const span = socMax - socMin || 1;
    const x = (s) => m.left + ((s - socMin) / span) * plotW;
    const yKw = (v) => m.top + plotH - (v / kwMax) * plotH;
    const yAmp = (v) => m.top + plotH - (v / ampMax) * plotH;
    return { socMin, socMax, span, kwMax, ampMax, x, yKw, yAmp };
  }, [curve, hasAmp]); // eslint-disable-line react-hooks/exhaustive-deps

  if (curve.length < 2) return null;
  const { socMin, socMax, span, kwMax, ampMax, x, yKw, yAmp } = geo;

  const grid = darkMode ? '#1e293b' : '#e7e5e4';
  const axis = darkMode ? '#334155' : '#d6d3d1';
  const muted = darkMode ? '#94a3b8' : '#78716c';

  // x ticks at multiples of 10 inside the domain
  const xTicks = [];
  for (let s = Math.ceil(socMin / 10) * 10; s <= socMax; s += 10) xTicks.push(s);
  const kwStep = kwMax > 300 ? 100 : 50;
  const kwTicks = [];
  for (let v = 0; v <= kwMax; v += kwStep) kwTicks.push(v);
  const ampStep = ampMax > 400 ? 100 : ampMax > 150 ? 50 : 20;
  const ampTicks = [];
  if (hasAmp) for (let v = 0; v <= ampMax; v += ampStep) ampTicks.push(v);

  const kwLine = curve.map((p) => `${x(p.soc)},${yKw(p.kw)}`).join(' ');
  const kwArea =
    `M ${x(curve[0].soc)},${m.top + plotH} ` +
    curve.map((p) => `L ${x(p.soc)},${yKw(p.kw)}`).join(' ') +
    ` L ${x(curve[curve.length - 1].soc)},${m.top + plotH} Z`;
  const ampPts = curve.filter((p) => p.amp !== null);
  const ampLine = ampPts.map((p) => `${x(p.soc)},${yAmp(p.amp)}`).join(' ');

  const handleMove = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * W;
    const soc = socMin + ((px - m.left) / plotW) * span;
    let best = 0, bestD = Infinity;
    curve.forEach((p, i) => {
      const d = Math.abs(p.soc - soc);
      if (d < bestD) { bestD = d; best = i; }
    });
    setHover(best);
  };

  const hp = hover !== null ? curve[hover] : null;

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${W} ${H}`}
      className="w-full h-auto select-none"
      onMouseMove={handleMove}
      onMouseLeave={() => setHover(null)}
      role="img"
      aria-label="Ladekurve"
    >
      {/* tiled JXMEDIA watermark (faint, repeated across the whole chart) */}
      <defs>
        <pattern id={wmId} width="190" height="150" patternUnits="userSpaceOnUse"
          patternTransform="rotate(-28)">
          <text x="8" y="80" fontSize="20" fontWeight="bold"
            fill={darkMode ? '#ffffff' : '#0f172a'}>JXMEDIA</text>
        </pattern>
      </defs>

      {/* grid */}
      {kwTicks.map((v) => (
        <line key={`gy${v}`} x1={m.left} y1={yKw(v)} x2={m.left + plotW} y2={yKw(v)}
          stroke={grid} strokeWidth="1" />
      ))}
      {xTicks.map((s) => (
        <line key={`gx${s}`} x1={x(s)} y1={m.top} x2={x(s)} y2={m.top + plotH}
          stroke={grid} strokeWidth="1" />
      ))}

      {/* kW area + line */}
      <path d={kwArea} fill={TEAL_FILL} opacity="0.14" />
      <polyline points={kwLine} fill="none" stroke={TEAL} strokeWidth="2.5"
        strokeLinejoin="round" strokeLinecap="round" />
      {/* Current line */}
      {hasAmp && (
        <polyline points={ampLine} fill="none" stroke={AMBER} strokeWidth="2"
          strokeLinejoin="round" strokeLinecap="round" />
      )}

      {/* watermark overlay (non-interactive, sits above the curve) */}
      <rect x="0" y="0" width={W} height={H} fill={`url(#${wmId})`}
        opacity="0.05" pointerEvents="none" />

      {/* axes */}
      <line x1={m.left} y1={m.top} x2={m.left} y2={m.top + plotH} stroke={axis} strokeWidth="1" />
      <line x1={m.left} y1={m.top + plotH} x2={m.left + plotW} y2={m.top + plotH}
        stroke={axis} strokeWidth="1" />
      {hasAmp && (
        <line x1={m.left + plotW} y1={m.top} x2={m.left + plotW} y2={m.top + plotH}
          stroke={axis} strokeWidth="1" />
      )}

      {/* tick labels */}
      {kwTicks.map((v) => (
        <text key={`ly${v}`} x={m.left - 8} y={yKw(v) + 4} textAnchor="end"
          fontSize="12" fill={TEAL}>{v}</text>
      ))}
      {xTicks.map((s) => (
        <text key={`lx${s}`} x={x(s)} y={m.top + plotH + 20} textAnchor="middle"
          fontSize="12" fill={muted}>{s}</text>
      ))}
      {hasAmp && ampTicks.map((v) => (
        <text key={`la${v}`} x={m.left + plotW + 8} y={yAmp(v) + 4} textAnchor="start"
          fontSize="12" fill={AMBER}>{v}</text>
      ))}

      {/* axis titles */}
      <text x={m.left} y={m.top - 8} fontSize="12" fontWeight="bold" fill={TEAL}>kW</text>
      {hasAmp && (
        <text x={m.left + plotW} y={m.top - 8} textAnchor="end" fontSize="12"
          fontWeight="bold" fill={AMBER}>A</text>
      )}
      <text x={m.left + plotW / 2} y={H - 8} textAnchor="middle" fontSize="12"
        fontWeight="bold" fill={muted}>Ladenivå (%)</text>

      {/* hover guide + markers + tooltip */}
      {hp && (
        <g>
          <line x1={x(hp.soc)} y1={m.top} x2={x(hp.soc)} y2={m.top + plotH}
            stroke={muted} strokeWidth="1" strokeDasharray="4 3" opacity="0.7" />
          <circle cx={x(hp.soc)} cy={yKw(hp.kw)} r="5" fill={TEAL}
            stroke={darkMode ? '#0f172a' : '#fff'} strokeWidth="2" />
          {hasAmp && hp.amp !== null && (
            <circle cx={x(hp.soc)} cy={yAmp(hp.amp)} r="5" fill={AMBER}
              stroke={darkMode ? '#0f172a' : '#fff'} strokeWidth="2" />
          )}
          {(() => {
            const flip = x(hp.soc) > m.left + plotW - 130;
            const bx = flip ? x(hp.soc) - 126 : x(hp.soc) + 12;
            const rows = 2 + (hasAmp && hp.amp !== null ? 1 : 0);
            return (
              <g>
                <rect x={bx} y={m.top + 6} width="114" height={20 + rows * 18} rx="8"
                  fill={darkMode ? '#0f172a' : '#ffffff'}
                  stroke={darkMode ? '#1e3a5f' : '#d6d3d1'} opacity="0.97" />
                <text x={bx + 10} y={m.top + 26} fontSize="12.5" fontWeight="bold"
                  fill={darkMode ? '#f0fdfa' : '#1c1917'}>{Math.round(hp.soc)} % ladet</text>
                <text x={bx + 10} y={m.top + 44} fontSize="12.5" fill={TEAL}>
                  {hp.kw.toFixed(0)} kW</text>
                {hasAmp && hp.amp !== null && (
                  <text x={bx + 10} y={m.top + 62} fontSize="12.5" fill={AMBER}>
                    {hp.amp.toFixed(0)} A</text>
                )}
              </g>
            );
          })()}
        </g>
      )}
    </svg>
  );
}
